import { OutcomeType } from '@/types';

/**
 * Meeting-Outcomes — EINZIGE Quelle fuer Optionen und Labels (Decision #114,
 * 15.07.2026; Darstellung siehe Decision #116, Variante B).
 *
 * Vorher lagen vier divergierende Label-Quellen im FE (CaseMeetingsTab,
 * meetings.json, meetingOutcome.json, customer/detail/MeetingsTab) mit
 * widersprechendem Wording — "Erfolgreich" vs. "Abgeschlossen", "Umterminiert"
 * vs. "Termin geaendert". Konvention (CLAUDE.md): kein i18n, hardcoded deutsch.
 *
 * Reihenfolge = Anzeige-Reihenfolge im Dropdown.
 *
 * KEINE Farb-Variante mehr: Das Outcome wird ueberall als Plain-Text-Label im
 * kanonischen `FilterSelect` gezeigt (Decision #116, Alex 15.07.). Die frueheren
 * Farb-Tags (StatusTag-Pille bzw. Hex-Tags der Uebersicht) sind entfallen —
 * `FilterSelect` rendert Text, und genau das ist der Projekt-Standard fuer
 * Auswahlfelder. Wer hier eine Farbe vermisst: das ist Absicht, siehe #116.
 */
export const OUTCOME_OPTIONS: { value: OutcomeType; label: string }[] = [
  { value: 'success', label: 'Erfolgreich' },
  { value: 'rescheduled', label: 'Umterminiert' },
  { value: 'no_show', label: 'Nicht erschienen' },
  { value: 'canceled_by_client', label: 'Abgesagt (Kunde)' },
  { value: 'canceled_by_advisor', label: 'Abgesagt (Berater)' },
];

/** Leerer/unbekannter Wert -> em-Dash (DESIGN_SYSTEM.md "Leere Werte"). */
export const getOutcomeLabel = (type?: string | null): string =>
  OUTCOME_OPTIONS.find((o) => o.value === type)?.label || '—';

/**
 * Terminabstimmungen (#113) sind stapelbare Berater-Erinnerungen ohne
 * Kundenkommunikation — sie haben KEIN Ergebnis (Decision #114 §C, Alex 15.07.).
 * Gilt fuer alle Outcome-Einstiegspunkte gleichermassen: kein Auswahlfeld,
 * stattdessen der em-Dash fuer "nicht anwendbar".
 */
export const hasOutcome = (meetingCategory?: string | null): boolean =>
  meetingCategory !== 'terminabstimmung';

/** Kennzeichnung am Badge und als aria-label — EIN Wortlaut, fuenf Ansichten. */
export const ZOOM_AUTO_HINWEIS = 'Ergebnis aus Zoom-Anwesenheit';

/**
 * Grund, den das Frontend nennt, wenn ein VP das Feld gesperrt vorfindet.
 * Wortgleich zur Ablehnung der Policy (`MeetingPolicy::setOutcome`) — zwei
 * Formulierungen fuer dieselbe Regel lesen sich wie zwei Regeln.
 */
export const ZOOM_AUTO_GESPERRT =
  'Dieses Ergebnis wurde automatisch aus der Zoom-Anwesenheit vergeben und kann nicht '
  + 'geändert werden. Bei einem falschen Ergebnis bitte an einen Administrator wenden.';

/**
 * Automatisch aus der Zoom-Anwesenheit vergeben (Auftrag Alex 09.08.2026)?
 *
 * `null`/`undefined` heisst „Herkunft unbekannt" (Alt-Bestand vor Einfuehrung
 * der Spalte) und ist ausdruecklich KEINE Automatik — sonst wuerde jeder alte
 * Termin ohne Herkunft gesperrt.
 */
export const istZoomAutomatik = (source?: string | null): boolean => source === 'zoom_auto';

/**
 * Grund der Sperre, oder `null` wenn das Ergebnis aenderbar ist.
 *
 * Die verbindliche Schranke ist die Policy im Backend; diese Funktion sagt
 * nur, was das Frontend anzeigt. Sie gibt bewusst den TEXT zurueck und kein
 * Boolean: ein gesperrtes Feld ohne Begruendung ist eine Fehlfunktion, keine
 * Regel — so kann keine Aufrufstelle die Sperre setzen und den Grund
 * vergessen.
 */
export const ergebnisSperre = (source?: string | null, istAdmin = false): string | null =>
  istZoomAutomatik(source) && !istAdmin ? ZOOM_AUTO_GESPERRT : null;
